<script setup lang="ts">
import { computed, ref } from 'vue'
import { te, t } from '@/locales/i18n'
import type { Filter, TFilterField, FilterCategory } from '@/types/api/reports'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import GwScrollCont from '@/components/lib/GwScrollCont/GwScrollCont.vue'
import PickerComponent from '@/components/hhv3/Analyzer/Filters/PickerComponent.vue'
import ExpandCard from '@/components/Common/ExpandCard/ExpandCard.vue'
import { getScreenUtils } from '@/components/lib/GwUtils/utils_screen'
import illustrationNoData from '@/assets/svg/illustrationNoData.svg'
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'
import { useLinks } from '@/composables/linksMix'
import { PortalTarget } from 'portal-vue'
import { getStreet } from '@/components/lib/GwTable/ActionSequence/actionSequenceUtils'
import GwSearch from '../../../DataTables/Filters/new/GwSearch.vue'
import GwText from '../../../lib/base/GwText/GwText.vue'
import RemoveButton from './RemoveButton.vue'

const { getUrlLinkHtml } = useLinks()

export interface FilterManagementProps {
  filterCategories: FilterCategory[]
  visibleFilters: Filter[]
}

export type FilterManagementEmits = {
  'remove:filter': [item: Filter]
  'update:filter': [item: Filter]
}

const props = withDefaults(defineProps<FilterManagementProps>(), {})
const emit = defineEmits<FilterManagementEmits>()

const allFilterCategories = computed(() => [
  { category_name: 'All', filters: props.filterCategories.flatMap(i => i.filters) },
  ...props.filterCategories,
])

const activeCategoryName = ref(allFilterCategories.value[0].category_name)
const searchedFilters = ref(null as TFilterField[] | null)

const activeCategory = computed(() =>
  allFilterCategories.value.find(i => i.category_name === activeCategoryName.value),
)

const activeCategoryVisibleFilters = computed(() =>
  activeCategory.value?.filters
    .filter(i => i.ui_value_type !== 'readonly_filename')
    .map(i => ({
      ...i,
      display_name: te(`hhv3.table_head.${i.field_name}`, i.field_name),
    })),
)

const currentFilters = computed(() => searchedFilters.value || activeCategoryVisibleFilters.value)
const currentFilterIds = computed(() => currentFilters.value?.map(({ id }) => id))

const visibleCreatedFilters = computed(() =>
  props.visibleFilters.filter(f => currentFilterIds.value?.includes(f.filter.id)),
)

const { mobile } = getScreenUtils()

const getActiveFilters = category =>
  category.filters.filter(i => props.visibleFilters.some(f => f.is_active && f.filter.id === i.id))
    .length
</script>
<template>
  <div
    class="f-column mh-0"
    style="background: var(--clr-black-0-1)"
    :style="
      mobile
        ? 'width: 100%'
        : `height: clamp(1px, 52rem, calc(100vw - 3rem)); width:100vw; max-width: clamp(1px, 69.25rem, calc(100vw - 6rem));`
    "
  >
    <div
      class="d-flex f-wrap"
      style="background: var(--clr-black-1); padding: 0.75rem; gap: 0.75rem"
    >
      <div class="text-normal weight_700 mr-auto my-auto">
        {{ t('hhv3.filter_management_title') }}
      </div>
      <GwSearch
        :items="activeCategoryVisibleFilters"
        :fuseOptions="{
          shouldSort: true,
          includeScore: true,
          includeMatches: true,
          threshold: 0.2,
          location: 0,
          distance: 75,
          maxPatternLength: 32,
          minMatchCharLength: 1,
          keys: ['display_name', 'ui_value_type'],
        }"
        data-tst="filter_search"
        style="min-width: clamp(1px, 18rem, calc(100vw - 2rem))"
        :placeholder="t('hhv3.search_filter')"
        @update:items="items => (searchedFilters = items)"
        @keyup="activeCategoryName = 'All'"
      />
    </div>
    <div
      class="mh-0"
      :class="mobile ? 'f-column' : 'f-row'"
      style="gap: 1rem; padding: 0.75rem"
    >
      <GwScrollCont
        :direction="mobile ? 'horizontal' : 'vertical'"
        class="mh-0 mw-0 no-shrink"
        stretched
        hasResizeObserver
        hasOverflowSurfaces
      >
        <div
          class="no-shrink"
          :class="mobile ? 'f-row' : 'f-column'"
          style="gap: 0.5rem; min-width: 12rem"
        >
          <GwBox
            v-for="category in allFilterCategories"
            :key="category.category_name"
            variant="box_text_grey_primary_big"
            class="d-flex"
            :data-tst="`btn_filter_category_${category.category_name}`"
            :active="activeCategoryName === category.category_name"
            @click.stop="activeCategoryName = category.category_name"
          >
            <span
              v-html="te(`hhv3.table_head.${category.category_name}`, category.category_name)"
            />
            <div class="ml-auto">{{ getActiveFilters(category) || '' }}</div>
          </GwBox>
        </div>
      </GwScrollCont>
      <GwScrollCont
        v-show="visibleCreatedFilters?.length"
        data-tst="column_management"
        direction="vertical"
        class="w-100 flex-1 mh-0 mw-0"
        stretched
        hasResizeObserver
        hasOverflowSurfaces
      >
        <div
          class="mh-0 mw-0 f-column flex-1"
          style="gap: var(--app-gap-4)"
        >
          <div
            class="f-column w-100"
            style="gap: var(--app-gap-4)"
          >
            <ExpandCard
              v-for="item in visibleFilters"
              v-show="currentFilterIds?.includes(item.filter.id)"
              :id="item.id"
              :key="item.id"
              :canClose="false"
              style="
                --overflow-content-open-x: auto;
                --clr-back-content: var(--clr-black-0-3);
                --max-height: 44rem;
                --radius: 0.5rem;
              "
              :data-tst="`filter_${item.filter.field_name}`"
              class="w-100"
            >
              <template #title>
                <div class="d-flex flex-column w-100 ga-1">
                  <div
                    class="d-flex flex-1"
                    :class="item.filter.filter_ui_type === 'ACTIONS_SEQUENCE' && 'mt-2'"
                  >
                    <div
                      class="my-auto text-normal weight_700 clr-white"
                      v-html="
                        te(
                          `hhv3.filter_bar.${item.filter.field_name}`,
                          te(`hhv3.table_head.${item.filter.field_name}`, item.filter.field_name),
                        )
                      "
                    />
                    <GwTooltipNew
                      v-if="t(`hhv3.tooltips.${item.filter.field_name}`)"
                      :closeOnContentHover="false"
                      :closeOnClick="false"
                      placement="right"
                      :tooltip="
                        t(`hhv3.tooltips.${item.filter.field_name}`, {
                          linkText: getUrlLinkHtml(item.filter.field_name),
                        })
                      "
                    >
                      <template #opener="{ listeners }">
                        <i
                          class="mdi mdi-help-circle-outline ml-1 my-auto"
                          v-on="listeners"
                        ></i>
                      </template>
                    </GwTooltipNew>
                    <RemoveButton
                      v-if="item.is_active"
                      class="ml-auto my-auto"
                      @remove:filter="
                        () => {
                          emit('remove:filter', item)
                        }
                      "
                    />
                  </div>
                  <portal-target
                    v-if="item.filter.filter_ui_type === 'ACTIONS_SEQUENCE'"
                    :name="`actions_sequence_line_${getStreet(item.filter.field_name)}`"
                  />
                </div>
              </template>
              <template #default="{}">
                <PickerComponent
                  :item
                  hideRemoveBtn
                  :data-tst="`filter_picker_${item.filter.field_name}`"
                  @remove:filter="
                    item => {
                      emit('remove:filter', item)
                    }
                  "
                  @update:filter="
                    item => {
                      const is_active = item.value !== null
                      emit('update:filter', { ...item, is_active })
                    }
                  "
                />
              </template>
            </ExpandCard>
          </div>
        </div>
      </GwScrollCont>
      <div
        v-if="!visibleCreatedFilters.length"
        class="f-column f-center ma-auto flex-1 mt-8 anim-fade-in"
        style="gap: var(--app-gap-2)"
        data-tst="no_filters"
      >
        <illustrationNoData style="max-width: 16rem" />
        <GwText variant="text_white_large_400">
          {{ t('hhv3.filter_management.no_filters') }}
        </GwText>

        <GwText
          variant="text_grey_normal_500"
          class="text-center"
          v-html="t('hhv3.filter_management.no_filters_sub')"
        />
      </div>
    </div>
  </div>
</template>
